import PageContentContainer from "../../Container/PageContentContainer";
import { ProductHeadline } from "../../PageHeadline/PageHeadline";
import ContentBox from "../../ContentBox/ContentBox";
import {
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCase } from "../../../provider/CaseProvider";
import LegalBirdPageLoading from "../../ContentLoader/LegalBirdPageLoading";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { HydraCollectionResult } from "../../../types/HydraCollectionResult";
import { HydraEntity } from "../../../types/HydraEntity";
import { useNavigate } from "react-router-dom";
import { translate } from "../../../services/Translations/translatorService";
import { ExecutionJobStatus } from "../../../types/ExecutionStatus";
import caseExecutionJobDetailsPageStyle from "./caseExecutionJobsPageStyle";
import dayjs from "dayjs";

interface ExecutionStep extends HydraEntity {
  id: number;
}

interface ExecutionJob extends HydraEntity {
  id: number;
  agentType: string;
  status: ExecutionJobStatus;
  requiresApproval: boolean;
  jobTitle: string | null;
  created: string | null;
  updated: string | null;
  steps: ExecutionStep[];
}

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "-";
  }

  return dayjs(value).format("DD.MM.YYYY HH:mm");
};

const CaseExecutionJobsPage = () => {
  const { product, isLoading: isCaseLoading } = useCase();
  const navigate = useNavigate();

  const caseIri = product?.backofficeCase?.["@id"]; //todo optional backofficeCase? + consider making product itself nonoptional
  const executionJobsFilter = { case: caseIri };

  const {
    data: executionJobs,
    isLoading: isExecutionJobsLoading,
    isError, //todo noone needs this
    refetch, //todo why not invalidate
  } = useQuery({
    queryKey: queryKeys.collection("execution_jobs", executionJobsFilter),
    queryFn: () =>
      fetchCollection("execution_jobs", executionJobsFilter) as Promise<HydraCollectionResult<ExecutionJob>>, //todo as
    enabled: !!caseIri, //todo not needed
  });

  if (isCaseLoading || !product || isExecutionJobsLoading) {
    return <LegalBirdPageLoading />;
  }

  return (
    <PageContentContainer>
      <ProductHeadline title="Execution Jobs von" product={product} />
      <ContentBox>
        {isError && (
          <Alert
            severity={"error"}
            action={
              <Button color={"inherit"} size={"small"} onClick={() => refetch()}>
                Neu laden
              </Button>
            }
          >
            Execution Jobs konnten nicht geladen werden.
          </Alert>
        )}
        {!isError && executionJobs && executionJobs["hydra:member"].length === 0 && (
          <Typography>Für diesen Fall sind noch keine Execution Jobs vorhanden.</Typography>
        )}
        {!isError && executionJobs && executionJobs["hydra:member"].length > 0 && (
          <TableContainer>
            <Table size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Titel</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Schritte</TableCell>
                  <TableCell>Freigabe nötig</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell>Aktualisiert</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executionJobs["hydra:member"].map((job) => (
                  <TableRow key={job.id} hover onClick={() => navigate(`${job.id}`)} sx={{ cursor: "pointer" }}>
                    <TableCell>{job.id}</TableCell>
                    <TableCell>{job.jobTitle || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size={"small"}
                        label={translate(`executionJob.status.${job.status}`, job.status)}
                        sx={caseExecutionJobDetailsPageStyle.jobStatusChipSxByStatus[job.status]}
                      />
                    </TableCell>
                    <TableCell>{job.agentType || "-"}</TableCell>
                    <TableCell>{job.steps?.length || 0}</TableCell>
                    <TableCell>{job.requiresApproval ? "Ja" : "Nein"}</TableCell>
                    <TableCell>{formatDateTime(job.created)}</TableCell>
                    <TableCell>{formatDateTime(job.updated)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ContentBox>
    </PageContentContainer>
  );
};

export default CaseExecutionJobsPage;
